// Central frontend API routing — single source of truth for where browser
// `/api/*` traffic is sent.
//
// In production, set VITE_API_URL to the Railway backend origin so the
// browser calls Railway directly — bypassing the Vercel rewrite proxy,
// which intermittently 503s under load. (The origin is configured via the
// VITE_API_URL env var, never hardcoded here.)
//
// In development (or when unset), this is empty string and API calls
// resolve as relative paths to the dev server / Vite proxy.
//
// Routing every call site through `apiUrl` / `apiFetch` is what lets a
// staging frontend target a staging backend purely via VITE_API_URL — with no
// per-call-site edits — instead of leaking to production through the hardcoded
// Vercel rewrite. See PR-INFRA-A.

// TASK-WEB-46 — SELECTIVE ENV READ. Read ONLY the variable this module uses.
//
// THE MECHANISM, stated once so it is never re-diagnosed:
// Vite replaces the STATIC member expression `import.meta.env.VITE_API_URL`
// with just that value's literal. Any WHOLE-OBJECT form — a bare
// `import.meta.env`, an alias (`const e = import.meta.env`), optional chaining
// (`import.meta.env?.X`), a spread, a destructure, or JSON.stringify — defeats
// that replacement, so Vite inlines the ENTIRE `VITE_*` map into the public
// client bundle. That is exactly how every git commit message shipped to every
// visitor until TASK-WEB-45 stripped the offending var.
//
// This module previously aliased the object to keep itself importable under
// plain tsx (where `import.meta.env` is undefined). The alias WAS the leak.
// The try/catch below buys the same tsx-importability with no object read:
// under Vite the member is already a literal and never throws; under tsx the
// read throws TypeError and we fall back to the same '' as before. Production
// behavior is identical — `API_BASE` is still VITE_API_URL or ''.
//
// NOT a substitute for the guard: `src/server/__tests__/bundleHygiene.smoke.ts`
// (TASK-WEB-45) still fails the build on internal content, and
// `envSelectiveRead.smoke.ts` fails if a whole-object read reappears anywhere.
function readApiBaseFromViteEnv(): string {
  try {
    return import.meta.env.VITE_API_URL || ''
  } catch {
    return ''
  }
}

export const API_BASE = readApiBaseFromViteEnv()

/**
 * Build an API URL from an `/api/...` path.
 *
 * Pure core — given an explicit base, returns the path unchanged when the base
 * is empty (dev / relative, handled by the Vite proxy or Vercel rewrite), or
 * the path prefixed with the normalized origin when the base is set (browser
 * calls that backend directly). Exported for unit testing.
 *
 * - Trailing slashes on `base` are stripped so the result never doubles a slash
 *   (`https://host/` + `/api/x` → `https://host/api/x`).
 * - Only paths starting with `/api/` are accepted, so an absolute URL or an
 *   already-prefixed path can't silently produce `/api/api/...` or leak to the
 *   wrong origin — it throws clearly instead.
 */
export function buildApiUrl(base: string, path: string): string {
  if (typeof path !== 'string' || !path.startsWith('/api/')) {
    throw new Error(`apiUrl: path must start with "/api/" (received: ${String(path)})`)
  }
  const normalizedBase = (base || '').replace(/\/+$/, '')
  return normalizedBase ? `${normalizedBase}${path}` : path
}

/**
 * Resolve an `/api/...` path against the configured API base (VITE_API_URL).
 * Empty base → relative path; set base → absolute URL to that origin.
 */
export function apiUrl(path: string): string {
  return buildApiUrl(API_BASE, path)
}

/**
 * Drop-in replacement for `fetch()` on API calls: `fetch(apiUrl(path), init)`.
 *
 * Only the URL is rewritten — every `RequestInit` option (method, headers,
 * body, credentials, signal, FormData uploads, …) and all response handling are
 * passed through untouched.
 */
export function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  return fetch(apiUrl(path), init).then((res) => {
    if (res.status === 402) notifyIfReadOnly(res)
    return res
  })
}

/** PR2: window event fired when the server answers 402 SUBSCRIPTION_READ_ONLY. */
export const READ_ONLY_EVENT = 'bluaiq:read-only'

// Reads a CLONE so the caller still gets an unread body. Never throws.
function notifyIfReadOnly(res: Response): void {
  if (typeof window === 'undefined' || typeof res.clone !== 'function') return
  res.clone().json().then((body: any) => {
    if (body?.code === 'SUBSCRIPTION_READ_ONLY') {
      window.dispatchEvent(new CustomEvent(READ_ONLY_EVENT, { detail: { reason: body.reason ?? null } }))
    }
  }).catch(() => {})
}
